import { useCallback, useEffect, useState } from "react";
import { Loader2, Megaphone, Pencil, Plus, Send, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api from "../api";
import { useLanguage } from "../i18n";

const EMPTY_PROMO = { code: "", kind: "percent", value: 10, valid_from: "", valid_to: "", active: true };
const EMPTY_CAMPAIGN = { name: "", subject: "", body: "", segment: "all" };
const EMPTY_BANNER = { active: false, title_it: "", title_en: "", text_it: "", text_en: "", cta_it: "", cta_en: "", link: "/destinations" };
const EMPTY_BLOGPROMO = { active: false, title: "", text: "", cta: "", link: "/destinations" };

const INPUT = "w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm outline-none focus:border-[#FF6B35]";
const LABEL = "font-mono-t text-[9px] font-bold uppercase tracking-widest text-slate-400";

/** Admin → Marketing: codici sconto, campagne email, banner home, promo blog. */
export function AdminMarketing() {
  const { t } = useLanguage();
  const [promos, setPromos] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [banner, setBanner] = useState(EMPTY_BANNER);
  const [blogPromo, setBlogPromo] = useState(EMPTY_BLOGPROMO);
  const [promoForm, setPromoForm] = useState(null);   // null | {…, editing: code?}
  const [campForm, setCampForm] = useState(null);
  const [estimate, setEstimate] = useState(null);
  const [busy, setBusy] = useState(false);

  const campSegment = campForm?.segment || null;
  const campOpen = Boolean(campForm);
  useEffect(() => {
    if (!campOpen || !campSegment) { setEstimate(null); return; }
    api.get(`/admin/campaigns/estimate?segment=${campSegment}`)
      .then(({ data }) => setEstimate(data.data.recipients))
      .catch(() => setEstimate(null));
  }, [campSegment, campOpen]);

  const load = useCallback(async () => {
    try {
      const [p, c, m] = await Promise.all([
        api.get("/admin/promos"), api.get("/admin/campaigns"), api.get("/admin/marketing/content")]);
      setPromos(p.data.data || []);
      setCampaigns(c.data.data || []);
      setBanner(m.data.data.home_banner || EMPTY_BANNER);
      setBlogPromo(m.data.data.blog_promo || EMPTY_BLOGPROMO);
    } catch {
      toast.error(t("misc.error"));
    }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  const savePromo = async () => {
    setBusy(true);
    try {
      const body = { ...promoForm, value: Number(promoForm.value) };
      delete body.editing;
      if (promoForm.editing) await api.put(`/admin/promos/${promoForm.editing}`, body);
      else await api.post("/admin/promos", body);
      toast.success(t("admin.saved"));
      setPromoForm(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || t("misc.error"));
    } finally {
      setBusy(false);
    }
  };

  const togglePromo = async (p) => {
    try {
      await api.put(`/admin/promos/${p.code}`, { ...p, active: !p.active });
      await load();
    } catch {
      toast.error(t("misc.error"));
    }
  };

  const saveCampaign = async () => {
    setBusy(true);
    try {
      if (campForm.campaign_id) await api.put(`/admin/campaigns/${campForm.campaign_id}`, campForm);
      else await api.post("/admin/campaigns", campForm);
      toast.success(t("admin.saved"));
      setCampForm(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || t("misc.error"));
    } finally {
      setBusy(false);
    }
  };

  const sendCampaign = async (c) => {
    if (!window.confirm(t("mkt.campaignSendConfirm").replace("{name}", c.name))) return;
    try {
      await api.post(`/admin/campaigns/${c.campaign_id}/send`, {});
      toast.success(t("mkt.campaignStarted"));
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || t("misc.error"));
    }
  };

  const testSendCampaign = async (c) => {
    try {
      const { data } = await api.post(`/admin/campaigns/${c.campaign_id}/test-send`, {});
      toast.success(t("mkt.testSent").replace("{email}", data.to));
    } catch (e) {
      toast.error(e.response?.data?.detail || t("misc.error"));
    }
  };

  const saveContent = async (kind, value) => {
    setBusy(true);
    try {
      await api.post(`/admin/marketing/${kind}`, value);
      toast.success(t("admin.saved"));
    } catch (e) {
      toast.error(e.response?.data?.detail || t("misc.error"));
    } finally {
      setBusy(false);
    }
  };

  const field = (value, setValue, key, label, testid, type = "text") => (
    <div key={key}>
      <label className={LABEL}>{label}</label>
      <input data-testid={testid} type={type} value={value[key] ?? ""} onChange={(e) => setValue({ ...value, [key]: e.target.value })}
        className={`mt-1 ${INPUT}`} />
    </div>
  );

  return (
    <div data-testid="admin-marketing-panel" className="space-y-6">
      {/* Codici sconto */}
      <section className="rounded-3xl bg-white border border-slate-200/80 p-6 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="font-display text-lg font-bold text-slate-900 flex items-center gap-2">
            <Megaphone className="w-5 h-5 text-[#FF6B35]" /> {t("mkt.promos")}
          </h2>
          <button type="button" data-testid="mkt-promo-new" onClick={() => setPromoForm({ ...EMPTY_PROMO })}
            className="inline-flex items-center gap-2 rounded-full bg-[#1E293B] text-white px-4 py-2 font-mono-t text-[10px] font-bold uppercase tracking-widest hover:bg-[#FF6B35] transition-colors">
            <Plus className="w-4 h-4" /> {t("mkt.promoNew")}
          </button>
        </div>
        {promoForm && (
          <div data-testid="mkt-promo-form" className="mt-4 rounded-2xl border border-slate-200 bg-slate-50/60 p-4 grid sm:grid-cols-3 gap-3">
            {field(promoForm, setPromoForm, "code", t("mkt.code"), "mkt-promo-code")}
            <div>
              <label className={LABEL}>{t("mkt.kind")}</label>
              <select data-testid="mkt-promo-kind" value={promoForm.kind} onChange={(e) => setPromoForm({ ...promoForm, kind: e.target.value })}
                className={`mt-1 ${INPUT}`}>
                <option value="percent">{t("mkt.kindPercent")}</option>
                <option value="fixed">{t("mkt.kindFixed")}</option>
              </select>
            </div>
            {field(promoForm, setPromoForm, "value", t("mkt.value"), "mkt-promo-value", "number")}
            {field(promoForm, setPromoForm, "valid_from", t("mkt.validFrom"), "mkt-promo-from", "date")}
            {field(promoForm, setPromoForm, "valid_to", t("mkt.validTo"), "mkt-promo-to", "date")}
            <div className="flex items-end gap-2">
              <button type="button" data-testid="mkt-promo-save" onClick={savePromo} disabled={busy}
                className="rounded-full bg-[#FF6B35] text-white px-5 py-2.5 font-mono-t text-[10px] font-bold uppercase tracking-widest disabled:opacity-50">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : t("admin.save")}
              </button>
              <button type="button" data-testid="mkt-promo-cancel" onClick={() => setPromoForm(null)}
                className="rounded-full border border-slate-200 bg-white px-5 py-2.5 font-mono-t text-[10px] font-bold uppercase tracking-widest text-slate-500">
                {t("id.cancelBtn")}
              </button>
            </div>
          </div>
        )}
        <div className="mt-4 space-y-2">
          {promos.length === 0 && <p className="text-sm text-slate-400">{t("mkt.promosEmpty")}</p>}
          {promos.map((p) => (
            <div key={p.code} data-testid={`mkt-promo-row-${p.code}`}
              className="flex flex-wrap items-center gap-x-4 gap-y-2 rounded-2xl border border-slate-200/80 px-5 py-3">
              <span className="font-mono-t text-sm font-bold text-slate-800">{p.code}</span>
              <span className="text-sm text-slate-500">
                {p.kind === "percent" ? `-${p.value}%` : `-€${p.value}`}
                {(p.valid_from || p.valid_to) && ` · ${p.valid_from || "…"} → ${p.valid_to || "…"}`}
                {` · ${t("mkt.uses")}: ${p.uses || 0}`}
              </span>
              <span className={`rounded-full px-2.5 py-1 font-mono-t text-[9px] font-bold uppercase tracking-widest ${
                p.active ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-400"}`}>
                {p.active ? t("mkt.active") : t("mkt.inactive")}
              </span>
              <span className="ml-auto flex gap-2">
                <button type="button" data-testid={`mkt-promo-edit-${p.code}`} onClick={() => setPromoForm({ ...p, valid_from: p.valid_from || "", valid_to: p.valid_to || "", editing: p.code })}
                  className="rounded-full border border-slate-200 px-3 py-1.5 font-mono-t text-[9px] font-bold uppercase tracking-widest text-slate-500 hover:border-[#FF6B35] hover:text-[#FF6B35] transition-colors">
                  <Pencil className="w-3.5 h-3.5" />
                </button>
                <button type="button" data-testid={`mkt-promo-toggle-${p.code}`} onClick={() => togglePromo(p)}
                  className="rounded-full border border-slate-200 px-3 py-1.5 font-mono-t text-[9px] font-bold uppercase tracking-widest text-slate-500 hover:border-[#FF6B35] hover:text-[#FF6B35] transition-colors">
                  {p.active ? t("mkt.deactivate") : t("mkt.activate")}
                </button>
              </span>
            </div>
          ))}
        </div>
      </section>

      {/* Campagne email */}
      <section className="rounded-3xl bg-white border border-slate-200/80 p-6 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="font-display text-lg font-bold text-slate-900">{t("mkt.campaigns")}</h2>
          <button type="button" data-testid="mkt-campaign-new" onClick={() => setCampForm({ ...EMPTY_CAMPAIGN })}
            className="inline-flex items-center gap-2 rounded-full bg-[#1E293B] text-white px-4 py-2 font-mono-t text-[10px] font-bold uppercase tracking-widest hover:bg-[#FF6B35] transition-colors">
            <Plus className="w-4 h-4" /> {t("mkt.campaignNew")}
          </button>
        </div>
        {campForm && (
          <div data-testid="mkt-campaign-form" className="mt-4 rounded-2xl border border-slate-200 bg-slate-50/60 p-4 space-y-3">
            <div className="grid sm:grid-cols-2 gap-3">
              {field(campForm, setCampForm, "name", t("mkt.campaignName"), "mkt-campaign-name")}
              <div>
                <label className={LABEL}>{t("mkt.segment")}</label>
                <select data-testid="mkt-campaign-segment" value={campForm.segment} onChange={(e) => setCampForm({ ...campForm, segment: e.target.value })}
                  className={`mt-1 ${INPUT}`}>
                  <option value="all">{t("mkt.segmentAll")}</option>
                  <option value="private">{t("mkt.segmentPrivate")}</option>
                  <option value="business">{t("mkt.segmentBusiness")}</option>
                </select>
                {estimate != null && (
                  <p data-testid="mkt-campaign-estimate" className="mt-1.5 text-xs font-medium text-[#0D9488]">
                    {t("mkt.estimate").replace("{n}", estimate)}
                  </p>
                )}
              </div>
            </div>
            {field(campForm, setCampForm, "subject", t("mkt.subject"), "mkt-campaign-subject")}
            <div>
              <label className={LABEL}>{t("mkt.body")}</label>
              <textarea data-testid="mkt-campaign-body" rows={6} value={campForm.body}
                onChange={(e) => setCampForm({ ...campForm, body: e.target.value })}
                placeholder={t("mkt.bodyHint")} className={`mt-1 ${INPUT}`} />
            </div>
            <div className="flex gap-2">
              <button type="button" data-testid="mkt-campaign-save" onClick={saveCampaign} disabled={busy}
                className="rounded-full bg-[#FF6B35] text-white px-5 py-2.5 font-mono-t text-[10px] font-bold uppercase tracking-widest disabled:opacity-50">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : t("admin.save")}
              </button>
              <button type="button" data-testid="mkt-campaign-cancel" onClick={() => setCampForm(null)}
                className="rounded-full border border-slate-200 bg-white px-5 py-2.5 font-mono-t text-[10px] font-bold uppercase tracking-widest text-slate-500">
                {t("id.cancelBtn")}
              </button>
            </div>
          </div>
        )}
        <div className="mt-4 space-y-2">
          {campaigns.length === 0 && <p className="text-sm text-slate-400">{t("mkt.campaignsEmpty")}</p>}
          {campaigns.map((c) => (
            <div key={c.campaign_id} data-testid={`mkt-campaign-row-${c.campaign_id}`}
              className="flex flex-wrap items-center gap-x-4 gap-y-2 rounded-2xl border border-slate-200/80 px-5 py-3">
              <span className="font-medium text-slate-800">{c.name}</span>
              <span className="text-sm text-slate-500">{c.subject} · {t(`mkt.segment${c.segment[0].toUpperCase()}${c.segment.slice(1)}`)}</span>
              {c.sent_at && (
                <span data-testid={`mkt-campaign-history-${c.campaign_id}`} className="text-xs text-slate-400">
                  {t("mkt.lastSent")}: {new Date(c.sent_at).toLocaleDateString()} · {t("mkt.delivered")}: {c.delivery_rate ?? 0}% ({c.sent_count}/{c.recipients_total || (c.sent_count + c.failed_count)})
                </span>
              )}
              <span className={`rounded-full px-2.5 py-1 font-mono-t text-[9px] font-bold uppercase tracking-widest ${
                c.status === "sent" ? "bg-emerald-50 text-emerald-700" : c.status === "sending" ? "bg-amber-50 text-amber-700" : "bg-slate-100 text-slate-500"}`}>
                {c.status === "sent" ? `${t("mkt.sent")}: ${c.sent_count}${c.failed_count ? ` · ${t("mkt.failed")}: ${c.failed_count}` : ""}` : c.status === "sending" ? `${t("mkt.sending")} ${c.sent_count}…` : t("mkt.draft")}
              </span>
              <span className="ml-auto flex gap-2">
                <button type="button" data-testid={`mkt-campaign-test-${c.campaign_id}`} onClick={() => testSendCampaign(c)}
                  title={t("mkt.testSend")}
                  className="rounded-full border border-[#0D9488] px-3 py-1.5 font-mono-t text-[9px] font-bold uppercase tracking-widest text-[#0D9488] hover:bg-emerald-50 transition-colors">
                  {t("mkt.testSend")}
                </button>
                <button type="button" data-testid={`mkt-campaign-edit-${c.campaign_id}`} onClick={() => setCampForm({ ...c })}
                  className="rounded-full border border-slate-200 px-3 py-1.5 font-mono-t text-[9px] font-bold uppercase tracking-widest text-slate-500 hover:border-[#FF6B35] hover:text-[#FF6B35] transition-colors">
                  <Pencil className="w-3.5 h-3.5" />
                </button>
                <button type="button" data-testid={`mkt-campaign-send-${c.campaign_id}`} onClick={() => sendCampaign(c)}
                  className="inline-flex items-center gap-1.5 rounded-full bg-[#1E293B] text-white px-3 py-1.5 font-mono-t text-[9px] font-bold uppercase tracking-widest hover:bg-[#FF6B35] transition-colors">
                  <Send className="w-3.5 h-3.5" /> {t("mkt.send")}
                </button>
              </span>
            </div>
          ))}
        </div>
      </section>

      {/* Banner home */}
      <section className="rounded-3xl bg-white border border-slate-200/80 p-6 shadow-sm">
        <div className="flex items-center justify-between gap-3">
          <h2 className="font-display text-lg font-bold text-slate-900">{t("mkt.banner")}</h2>
          <button type="button" data-testid="mkt-banner-active" onClick={() => setBanner({ ...banner, active: !banner.active })}
            className={`rounded-full px-4 py-2 font-mono-t text-[10px] font-bold uppercase tracking-widest transition-colors ${
              banner.active ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-400"}`}>
            {banner.active ? "ON" : "OFF"}
          </button>
        </div>
        <div className="mt-4 grid sm:grid-cols-2 gap-3">
          {field(banner, setBanner, "title_it", "Titolo (IT)", "mkt-banner-title-it")}
          {field(banner, setBanner, "title_en", "Title (EN)", "mkt-banner-title-en")}
          {field(banner, setBanner, "text_it", "Testo (IT)", "mkt-banner-text-it")}
          {field(banner, setBanner, "text_en", "Text (EN)", "mkt-banner-text-en")}
          {field(banner, setBanner, "cta_it", "Pulsante (IT)", "mkt-banner-cta-it")}
          {field(banner, setBanner, "cta_en", "Button (EN)", "mkt-banner-cta-en")}
          {field(banner, setBanner, "link", t("mkt.link"), "mkt-banner-link")}
        </div>
        <button type="button" data-testid="mkt-banner-save" onClick={() => saveContent("home-banner", banner)} disabled={busy}
          className="mt-4 rounded-full bg-[#FF6B35] text-white px-5 py-2.5 font-mono-t text-[10px] font-bold uppercase tracking-widest disabled:opacity-50">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : t("admin.save")}
        </button>
      </section>

      {/* Promo blog */}
      <section className="rounded-3xl bg-white border border-slate-200/80 p-6 shadow-sm">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h2 className="font-display text-lg font-bold text-slate-900">{t("mkt.blogPromo")}</h2>
            <p className="text-xs text-slate-400">{t("mkt.blogPromoHint")}</p>
          </div>
          <button type="button" data-testid="mkt-blogpromo-active" onClick={() => setBlogPromo({ ...blogPromo, active: !blogPromo.active })}
            className={`rounded-full px-4 py-2 font-mono-t text-[10px] font-bold uppercase tracking-widest transition-colors ${
              blogPromo.active ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-400"}`}>
            {blogPromo.active ? "ON" : "OFF"}
          </button>
        </div>
        <div className="mt-4 grid sm:grid-cols-2 gap-3">
          {field(blogPromo, setBlogPromo, "title", t("mkt.title"), "mkt-blogpromo-title")}
          {field(blogPromo, setBlogPromo, "cta", t("mkt.cta"), "mkt-blogpromo-cta")}
          <div className="sm:col-span-2">{field(blogPromo, setBlogPromo, "text", t("mkt.bodyShort"), "mkt-blogpromo-text")}</div>
          {field(blogPromo, setBlogPromo, "link", t("mkt.link"), "mkt-blogpromo-link")}
        </div>
        <button type="button" data-testid="mkt-blogpromo-save" onClick={() => saveContent("blog-promo", blogPromo)} disabled={busy}
          className="mt-4 rounded-full bg-[#FF6B35] text-white px-5 py-2.5 font-mono-t text-[10px] font-bold uppercase tracking-widest disabled:opacity-50">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : t("admin.save")}
        </button>
      </section>
    </div>
  );
}
